import useModal from '@area2k/use-modal';
import { differenceInHours, parseISO } from 'date-fns';
import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import ConfirmFinalizeDraft from '../ConfirmFinishDraft';
import ConfirmVoidInvoice from '../ConfirmVoidInvoice';
import PageSkeleton from '../Skeletons/DetailsSkeleton';
import ErrorSkeleton from '../Skeletons/ErrorSkeleton';

import DetailsCard from './DetailsCard';
import ItemsCard from './ItemsCard';
import ToggleAutoApprove from './ToggleAutoApprove';
import TotalsCard from './TotalsCard';

import Button from '@/components/Button';
import Page from '@/components/Page';
import PrimarySecondaryLayout from '@/components/PrimarySecondaryLayout';
import Stack from '@/components/Stack';
import { UNKNOWN_ERROR_TEXT } from '@/constants/text';
import {
  useGetInvoiceDetailsQuery,
  useToggleAutoApproveMutation,
} from '@/graphql';
import useAuth from '@/hooks/useAuth';
import { GenericAction, GenericActionOrList } from '@/types';
import { StripeInvoiceStatusEnum } from '@/types/graphql';

const InvoiceDetail = () => {
  const navigate = useNavigate();
  const { currentAdminIsCustomerAdmin } = useAuth();
  const { invoiceId } = useParams();
  const { state, search } = useLocation();
  const [autoApprove, setAutoApprove] = useState<boolean>(true);
  const [autoApproveDisabled, setAutoApproveDisabled] = useState<boolean>(true);
  const [autoApproveError, setAutoApproveError] = useState(null);
  const { loading, error, data } = useGetInvoiceDetailsQuery({
    variables: {
      invoiceId,
    },
  });

  const [toggleAutoApprove, { loading: toggleLoading }] =
    useToggleAutoApproveMutation();

  const [showConfirmFinishDraftModal, hideConfirmFinishDraftModal] = useModal(
    () => (
      <ConfirmFinalizeDraft
        hideModal={hideConfirmFinishDraftModal}
        invoiceId={invoiceId}
      />
    ),
    [invoiceId]
  );

  const [showConfirmVoidInvoiceModal, hideConfirmVoidInvoiceModal] = useModal(
    () => (
      <ConfirmVoidInvoice
        hideModal={hideConfirmVoidInvoiceModal}
        invoiceId={invoiceId}
      />
    ),
    [invoiceId]
  );

  useEffect(() => {
    if (
      data &&
      data.listStripeInvoiceDetail?.invoice?.status ===
        StripeInvoiceStatusEnum.DRAFT
    ) {
      const { invoice } = data.listStripeInvoiceDetail;
      setAutoApprove(invoice.autoApprove);
      const hours = differenceInHours(
        new Date(),
        parseISO(invoice.createdDatetime)
      );
      setAutoApproveDisabled(hours > 48);
    }
  }, [data]);

  if (loading) {
    return <PageSkeleton />;
  }

  if (error || !data) {
    return <ErrorSkeleton />;
  }

  const { invoice, invoiceItems } = data.listStripeInvoiceDetail;
  const invoiceIsOpen = invoice.status === StripeInvoiceStatusEnum.OPEN;
  const invoiceIsDraft = invoice.status === StripeInvoiceStatusEnum.DRAFT;
  const pagePrimaryAction: GenericAction | undefined = {
    a11yLabel: 'Pay Invoice',
    label: 'Pay Invoice',
    onAction: () => navigate('pay' + search),
    id: 'btn-pay-invoice',
  };

  const invoiceActions = (
    status: StripeInvoiceStatusEnum
  ): GenericActionOrList[] | undefined => {
    const invoiceAction = {
      [StripeInvoiceStatusEnum.DRAFT]: {
        a11yLabel: 'Finish Draft',
        label: 'Finish Draft',
        onAction: showConfirmFinishDraftModal,
        id: 'btn-finish-draft',
      },
      [StripeInvoiceStatusEnum.OPEN]: {
        a11yLabel: 'Void',
        label: 'Void',
        dangerous: true,
        onAction: showConfirmVoidInvoiceModal,
        id: 'btn-void',
      },
    }[status];

    return invoiceAction ? [invoiceAction] : undefined;
  };

  const onToggleChanged = async (value) => {
    try {
      setAutoApprove(value);
      setAutoApproveError(null);
      await toggleAutoApprove({
        variables: {
          invoiceId,
        },
      });
    } catch (error: any) {
      setAutoApprove(!value);
      setAutoApproveError(error.message ?? UNKNOWN_ERROR_TEXT);
    }
  };

  return (
    <Page
      primaryAction={invoiceIsOpen ? pagePrimaryAction : undefined}
      secondaryActions={
        !currentAdminIsCustomerAdmin
          ? invoiceActions(invoice.status)
          : undefined
      }
      size="xl"
      title={`Invoice #${invoice.id}${
        invoice.number ? `-${invoice.number}` : ''
      }`}
    >
      {invoiceIsDraft && (
        <>
          <ToggleAutoApprove
            checked={autoApprove}
            disabled={toggleLoading || autoApproveDisabled}
            error={autoApproveError}
            onToggleChange={onToggleChanged}
          />
        </>
      )}
      <Stack vertical gap={30}>
        <DetailsCard
          editable={invoiceIsDraft && !currentAdminIsCustomerAdmin}
          invoice={invoice}
        />
        <ItemsCard
          editable={invoiceIsDraft && !currentAdminIsCustomerAdmin}
          invoice={invoice}
          items={invoiceItems}
        />
        <PrimarySecondaryLayout flipped>
          <PrimarySecondaryLayout.Primary>
            <Stack justify="end">
              <TotalsCard invoice={invoice} />
            </Stack>
          </PrimarySecondaryLayout.Primary>
        </PrimarySecondaryLayout>
        <Stack justify="end">
          <Button
            a11yLabel="Go back"
            appearance="primary"
            id="btn-go-back"
            label="Go Back"
            type="button"
            onClick={() => navigate(state?.from ? state.from : '../' + search)}
          />
        </Stack>
      </Stack>
    </Page>
  );
};

export default InvoiceDetail;
